import { Accordion, Card, useAccordionToggle } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";

// Recursive component for nested categories (same logic as SidebarItem)
const MobileSidebarItem = ({
  nodes,
  eventKeyPrefix = "",
  onSelect,
  selectedCategoryId,
  selectedSubCategoryId,
  depth = 0
}) => {
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  // Helper function — recursively checks if a node or its descendants match the ID
  const findInTree = (node, targetId) => {
    if (node.id === targetId) return true;

    return node.sub_categories?.some((sub) => findInTree(sub, targetId)) ?? false;
  }

  // Automatically expand the parent if it contains the selected subcategory
  useEffect(() => {
    const targetId = selectedCategoryId || selectedSubCategoryId;

    if (!nodes || !targetId) {
      setActiveKey(undefined);

      return;
    };

    const parentNode = nodes.find((node) => 
      node.sub_categories?.length > 0 && findInTree(node, targetId)
    );

    if (parentNode) {
      setActiveKey(eventKeyPrefix + parentNode.id);
    }
  }, [nodes, selectedCategoryId, selectedSubCategoryId, eventKeyPrefix]);

  if (!nodes || nodes.length === 0) return null;

  const CustomToggle = ({ eventKey, node, isExpanded, isSelected, hasSubCategories }) => {
    const decoratedOnClick = useAccordionToggle(eventKey, () => {
      setActiveKey(isExpanded ? undefined : String(eventKey));
    });

    return (
      <Card.Header
        className={`sidebar-category-header mobile-category ${isSelected ? "selected" : ""} ${depth > 0 ? "sidebar-subcategory mobile-subcategory" : ""}`}
        style={{ paddingLeft: depth > 0 ? `${20 + (depth * 15)}px` : '16px' }}
        onClick={(e) => {
          onSelect(node);
          if (hasSubCategories) {
            decoratedOnClick(e);
          } else {
            setActiveKey(undefined);
          }
        }}
      >
        <span className="category-name">{node.name}</span>
        {hasSubCategories && (
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="chevron-icon"
          />
        )}
      </Card.Header>
    );
  };

  return (
    <Accordion activeKey={activeKey}>
      {nodes.map((node) => {
        const eventKey = eventKeyPrefix + node.id;
        const isExpanded = activeKey === String(eventKey);
        const isSelected = selectedSubCategoryId ? selectedSubCategoryId === node.id : selectedCategoryId === node.id;
        const hasSubCategories = node.sub_categories && node.sub_categories.length > 0;

        return (
          <Card key={eventKey} className="sidebar-card mobile-category-card">
            <CustomToggle 
              eventKey={String(eventKey)} 
              node={node}
              isExpanded={isExpanded}
              isSelected={isSelected}
              hasSubCategories={hasSubCategories}
            />

            {hasSubCategories && (
              <Accordion.Collapse eventKey={String(eventKey)}>
                <Card.Body className="p-0">
                  <MobileSidebarItem
                    nodes={node.sub_categories}
                    eventKeyPrefix={eventKey + "-"}
                    onSelect={onSelect}
                    selectedCategoryId={selectedCategoryId}
                    selectedSubCategoryId={selectedSubCategoryId}
                    depth={depth + 1}
                  />
                </Card.Body>
              </Accordion.Collapse>
            )}
          </Card>
        );
      })}
    </Accordion>
  );
}

const SidebarMobile = ({
  categories,
  onCategorySelect,
  selectedCategoryId,
  selectedSubCategoryId,
}) => {
  const [mainAccordionOpen, setMainAccordionOpen] = useState(false);

  if (!categories || categories.length === 0) return null;

  const MainToggle = () => {
    const decoratedOnClick = useAccordionToggle("0", () => {
      setMainAccordionOpen(!mainAccordionOpen);
    });

    return (
      <Card.Header
        className="mobile-sidebar-header"
        onClick={decoratedOnClick}
      >
        <span className="mobile-sidebar-title">Categories</span>
        <FontAwesomeIcon
          icon={mainAccordionOpen ? faChevronUp : faChevronDown}
          className="chevron-icon-mobile"
        />
      </Card.Header>
    );
  };

  return (
    <Accordion className="d-md-none mb-3 mobile-sidebar-wrapper">
      <Card className="mobile-sidebar-card">
        <MainToggle />

        <Accordion.Collapse eventKey="0">
          <Card.Body className="p-0">
            <MobileSidebarItem
              nodes={categories}
              eventKeyPrefix=""
              onSelect={onCategorySelect}
              selectedCategoryId={selectedCategoryId}
              selectedSubCategoryId={selectedSubCategoryId}
              depth={0}
            />
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};

export default SidebarMobile;
